import type { RFileUploadResponse } from 'v2/types/api/auth'
import { z } from 'zod'
import { KBDocumentSource, type NewKBFormValues } from './types'

export const MAX_FILES_ALLOWED = 20
// Schema Definition
export const dataSourceBaseSchema = z.object({
  type: z.nativeEnum(KBDocumentSource, { required_error: 'Please select a data source type' }),
  url_selection_type: z.enum(['all', 'selected']),
  files: z
    .array(z.custom<RFileUploadResponse>())
    .min(1, { message: 'Please upload at least one file' })
    .max(MAX_FILES_ALLOWED, { message: `Maximum ${MAX_FILES_ALLOWED} files allowed` }),
  links: z.array(
    z.object({
      title: z.string(),
      url: z.string(),
      selected: z.boolean(),
      markdown: z.string(),
      id: z.string(),
    }),
  ),
  app_data: z.array(
    z.object({
      type: z.enum(['article', 'collection']),
      id: z.string(),
    }),
  ),
  app_source: z.string().nullable().optional(),
  app_source_id: z.string().nullable().optional(),
  appLoading: z.boolean().default(false),
})

export const dataSourceSchema = dataSourceBaseSchema.superRefine((data, ctx) => {
  if (data.type === KBDocumentSource.URL && data.url_selection_type === 'selected') {
    if (!data.links.some((link) => link.selected)) {
      ctx.addIssue({
        path: ['links'],
        code: z.ZodIssueCode.custom,
        message: 'Please select at least one URL',
      })
    }
  }

  if (data.type === KBDocumentSource.APP) {
    if (!data.app_source) {
      ctx.addIssue({
        path: ['app_source'],
        code: z.ZodIssueCode.custom,
        message: 'Please select an app source',
      })
    }

    if (!data.app_source_id) {
      ctx.addIssue({
        path: ['app_source_id'],
        code: z.ZodIssueCode.custom,
        message: 'Please select an app source',
      })
    }

    if (data.url_selection_type === 'selected' && data.app_data.length === 0) {
      ctx.addIssue({
        path: ['app_data'],
        code: z.ZodIssueCode.custom,
        message: 'Please select at least one document',
      })
    }
  }
})

// Schema Definition
export const documentSetupSchema = z.object({
  name: z.string().min(1, 'Knowledge base name is required'),
  agents: z.array(z.string()).optional(),
  // Retrieval profile (rag `profile`): 'default' = top-5, voice-friendly; 'docs' = top-20 +
  // long-query rewrite for large documentation corpora on the chat channel (higher latency).
  profile: z.enum(['default', 'docs']).default('default'),
  // Match the opt-in form default (newKBFormDefaultValues): exclude image/YouTube enrichment unless the
  // user opts in. Keeping these at .default(false) would silently re-enable enrichment if the field ever
  // reached the parser undefined.
  ignore_youtube_content: z.boolean().default(true),
  ignore_image_content: z.boolean().default(true),
  sync_frequency: z
    .object({
      sync_period: z.enum(['WEEK', 'MONTH']),
      sync_frequency: z.number({ message: 'Frequency value is required' }),
    })
    .superRefine((val, ctx) => {
      if (val.sync_period === 'WEEK' && val.sync_frequency < 2)
        ctx.addIssue({
          code: z.ZodIssueCode.too_small,
          minimum: 2,
          type: 'number',
          inclusive: true,
          path: ['sync_frequency'],
          message: 'Sync frequency value cannot be less than "2"',
        })
      if (val.sync_period !== 'WEEK' && val.sync_frequency < 1)
        ctx.addIssue({
          code: z.ZodIssueCode.too_small,
          minimum: 1,
          type: 'number',
          inclusive: true,
          path: ['sync_frequency'],
          message: 'Sync frequency value cannot be less than "1"',
        })
    }),
  url_selection_type: z.enum(['all', 'selected']).default('all'),
  sync_enabled: z.boolean().default(false),
})

export const websiteScraperSchema = z.object({
  url: z.string().url('Please enter a valid URL'),
  // Hard caps mirror the rag SourceSpec clamp (depth<=30, pages<=2000) so the UI can't request a
  // crawl the backend will silently shrink. Defaults (depth=5, pages=500) live in the form values.
  max_depth: z.number({ invalid_type_error: 'Please enter a valid number' }).min(1).max(30),
  max_pages: z.number({ invalid_type_error: 'Please enter a valid number' }).min(1).max(2000),
  crawl_subpages: z.boolean(),
  exclude_paths: z.array(z.string()),
  include_paths: z.array(z.string()),
})

export const newKBFormDefaultValues: NewKBFormValues = {
  step: 1,
  name: '',
  agents: [],
  profile: 'default' as const,
  // Default to EXCLUDING image/YouTube enrichment: these are expensive vision/LLM passes (the crawler
  // fetches + captions every image on every page), which made image-heavy site crawls very slow / look
  // stuck. Opt-in — the user unchecks "Exclude images/videos from processing" to turn it on per-KB.
  ignore_image_content: true,
  ignore_youtube_content: true,
  sync_frequency: {
    sync_period: 'WEEK' as const,
    sync_frequency: 2,
  },
  sync_enabled: false,
  url_selection_type: 'all' as const,
  type: KBDocumentSource.FILE,
  url: '',
  files: [],
  links: [],
  jobId: '',
  // Website scraper fields
  isLoading: false,
  optionsAccordionOpen: ['options'],
  max_depth: 5,
  max_pages: 500,
  crawl_subpages: false,
  exclude_paths: [],
  include_paths: [],
  app_data: [],
  app_source: null,
  appLoading: false,
}

export const KBSources = ['notion', 'confluence', 'freshdesk', 'zendesk', 'intercom']
